import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Form, Modal, Radio, Tooltip,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { generateMonitoringOrdersCo2EmissionReport } from '../../../store/actions/reports';
import { StyledRangePicker } from '../../../components/Styled/Custom';
import { DEFAULT_DATE_TIME, DEFAULT_TIME } from '../../../utils/constants/timeFormats';
import { isSuccess } from '../../../utils/responseUtils';

const WEEK_MINUTES = 7 * 24 * 60;

const FormItem = styled(Form.Item)`
  width: 338px;
  .ant{
    &-picker {
      width: 100%;
    }
  }
`;

const RadioLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const Co2EmissionReportModal = ({
  visible, onCancel, contextCompanyId,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([]);

  useEffect(() => {
    if (visible) {
      form.setFields([
        { name: 'granularity', value: 'LEG' },
      ]);
      return;
    }
    setLoading(false);
    setDateRange([]);
    form.resetFields();
  }, [form, visible]);

  const handleClose = () => {
    setDateRange([]);
    form.resetFields();
    onCancel();
  };

  const disabledDate = (current) => current && dateRange && !(dateRange[0] && dateRange[1]) && (
    (dateRange[0] && dateRange[0].clone().add(7, 'd') <= current)
    || (dateRange[1] && dateRange[1].clone().subtract(7, 'd') >= current));

  const timeRangeValidator = () => {
    const startDate = form.getFieldValue('dateRange')?.[0];
    const endDate = form.getFieldValue('dateRange')?.[1];
    if (startDate && endDate) {
      const dateRangeInMinutes = endDate.diff(startDate, 'minutes');
      if (dateRangeInMinutes > WEEK_MINUTES) {
        return Promise.reject();
      }
    }
    return Promise.resolve();
  };

  const handleOk = () => {
    form.validateFields()
      .then((values) => {
        setLoading(true);
        const requestParams = {
          contextCompanyId,
          dateFrom: values.dateRange[0].unix(),
          dateTo: values.dateRange[1].unix(),
          granularity: values.granularity,
        };

        dispatch(generateMonitoringOrdersCo2EmissionReport(requestParams))
          .then((response) => {
            if (isSuccess(response)) {
              handleClose();
            }
          })
          .finally(() => {
            setLoading(false);
          });
      });
  };

  return (
    <Modal
      destroyOnClose
      open={visible}
      title={t('CO2_EMISSION_REPORT.MODAL.TITLE')}
      onOk={handleOk}
      onCancel={handleClose}
      confirmLoading={loading}
      width={386}
      okText={t('CO2_EMISSION_REPORT.MODAL.DOWNLOAD')}
      cancelText={t('CO2_EMISSION_REPORT.MODAL.CANCEL')}
    >
      <Form
        form={form}
        layout="vertical"
        name="co2EmissionReport"
      >
        <FormItem
          name="dateRange"
          label={t('CO2_EMISSION_REPORT.MODAL.TIME_RANGE')}
          rules={[
            {
              required: true,
              message: t('CO2_EMISSION_REPORT.MODAL.PLEASE_PROVIDE_TIME_RANGE'),
            }, {
              validator: timeRangeValidator,
              message: t('CO2_EMISSION_REPORT.MODAL.TIME_RANGE_CAN_BE_MAX_7_DAYS'),
            },
          ]}
        >
          <StyledRangePicker
            showTime={{ format: DEFAULT_TIME }}
            format={DEFAULT_DATE_TIME}
            onChange={setDateRange}
            onOk={setDateRange}
            disabledDate={disabledDate}
            dropdownClassName="ant-picker-shadow-2"
          />
        </FormItem>
        <FormItem
          name="granularity"
          label={t('CO2_EMISSION_REPORT.MODAL.GRANULARITY')}
          rules={[
            {
              required: true,
              message: t('CO2_EMISSION_REPORT.MODAL.PLEASE_SELECT_GRANULARITY'),
            },
          ]}
        >
          <Radio.Group>
            <Radio value="LEG">
              <RadioLabel>
                {t('CO2_EMISSION_REPORT.MODAL.GRANULARITY_OPTIONS.LEG')}
                <Tooltip title={t('CO2_EMISSION_REPORT.MODAL.GRANULARITY_TOOLTIPS.LEG')}>
                  <InfoCircleOutlined />
                </Tooltip>
              </RadioLabel>
            </Radio>
            <Radio value="ORDER">
              <RadioLabel>
                {t('CO2_EMISSION_REPORT.MODAL.GRANULARITY_OPTIONS.ORDER')}
                <Tooltip title={t('CO2_EMISSION_REPORT.MODAL.GRANULARITY_TOOLTIPS.ORDER')}>
                  <InfoCircleOutlined />
                </Tooltip>
              </RadioLabel>
            </Radio>
          </Radio.Group>
        </FormItem>
      </Form>
    </Modal>
  );
};

Co2EmissionReportModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  contextCompanyId: PropTypes.string.isRequired,
};

export default Co2EmissionReportModal;
